import React, { ReactElement, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ArticleDto } from '../../api/ClientApi';
import ArticleAutocomplete from '../../common/components/autocomplete/ArticleAutocomplete';
import { createProductTemplate, ProductTemplateModel, updateProductTemplate } from '../../api/Extensions';

import { FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

type ProductTemplateFormProps = {
    open: boolean;
    template?: ProductTemplateModel;
    articles: ArticleDto[];
    onClose(): void;
    onSaved(): void;
};

interface FormItem {
    key: number;
    articleId?: number;
    quantity: number;
}

export default function ProductTemplateForm(props: ProductTemplateFormProps): ReactElement {
    const { open, template, articles, onClose, onSaved } = props;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [items, setItems] = useState<FormItem[]>([]);
    const [nextKey, setNextKey] = useState(1);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(template?.title ?? '');
            setDescription(template?.description ?? '');
            const seeded = (template?.items ?? []).map((it, index) => ({
                key: index + 1,
                articleId: it.articleId,
                quantity: it.quantity,
            }));
            setItems(seeded);
            setNextKey(seeded.length + 1);
        }
    }, [open, template]);

    const updateItem = (key: number, patch: Partial<FormItem>) => {
        setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
    };

    const addRow = () => {
        setItems((prev) => [...prev, { key: nextKey, quantity: 1, articleId: undefined }]);
        setNextKey((k) => k + 1);
    };

    const removeRow = (key: number) => {
        setItems((prev) => prev.filter((i) => i.key !== key));
    };

    const isValid = title.trim() !== '' && description.trim() !== '';

    const handleSave = async () => {
        if (!isValid) {
            return;
        }

        const payload: ProductTemplateModel = {
            title: title.trim(),
            description: description.trim(),
            items: items
                .filter((i) => !!i.articleId && i.articleId > 0)
                .map((i) => ({ articleId: i.articleId as number, quantity: i.quantity })),
        };

        setSaving(true);
        try {
            if (template?.id) {
                await updateProductTemplate(template.id, payload);
            } else {
                await createProductTemplate(payload);
            }
            onSaved();
        } catch {
            alert('Došlo je do greške pri čuvanju šablona.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <FormDialogTitle>{template?.id ? 'Izmena šablona' : 'Novi šablon'}</FormDialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    required
                    fullWidth
                    margin="dense"
                    label="Naslov"
                    value={title}
                    error={title.trim() === ''}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    margin="dense"
                    label="Opis (glavni elementi)"
                    value={description}
                    error={description.trim() === ''}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Artikli
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '70%' }}>Artikal</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>
                                Količina
                            </TableCell>
                            <TableCell align="right" sx={{ width: 50 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <TableRow key={item.key}>
                                <TableCell>
                                    <ArticleAutocomplete
                                        isRequired
                                        articles={articles}
                                        selectedArticleId={item.articleId}
                                        selectArticle={(articleId) => updateItem(item.key, { articleId })}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        type="number"
                                        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                        size="small"
                                        value={item.quantity}
                                        onChange={(e) =>
                                            updateItem(item.key, { quantity: Number(e.target.value) || 0 })
                                        }
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => removeRow(item.key)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    Još nema artikala.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <Box sx={{ mt: 1 }}>
                    <Button startIcon={<AddIcon />} onClick={addRow}>
                        Dodaj artikal
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Odustani
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!isValid || saving}>
                    {saving ? <CircularProgress size={16} color="inherit" /> : 'Sačuvaj'}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}
